import { RequestClass, ShareRequest } from 'acscp/api/requests';
import { FormattedMessage } from 'react-intl';
import { Dispatch } from 'redux';
import { AddNotification, FetchRequest, FetchResetStatus } from 'acscp/actions';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { AppState, SharingType } from 'acscp/store';
import { defineMessages, InjectedIntlProps, injectIntl } from 'react-intl';
import { ErrorsDisplay } from 'acscp/components/common/errorsDisplay';
import { RequestErrorMessage } from 'acscp/components/common/requestErrorMessage';
import React from 'react';
import { ButtonWithLoadingIndicator } from 'acscp/components/common/buttonWithLoadingIndicator';
import style from './sharing.module.scss';
import TagsInput from 'react-tagsinput';
import isEmail from 'validator/lib/isEmail';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { getUniqueElements } from 'acscp/components/common/helpers/getUniqueElements';
import { EmailHistoryDisplay } from './emailHistoryDisplay';
import { NotificationType } from 'acscp/store/notificationState';
import { LocaleCode } from 'acscp/translations';

/**
 * Monkey patch the react TagsInput clipboard handling code.
 * The handlePaste function is more or less unchanged (just added
 * types) but overriding it is the easiest way to replace this
 * function: getClipboardData. Handling paste events varies
 * across browsers and the default implementation did not
 * work for our iOS app: the type is "public.text" rather
 * than "text/plain", but you can get this from the types
 * array.
 */
function getClipboardData(e: ClipboardEvent): string | undefined {
    if ((window as any).clipboardData) {
        return (window as any).clipboardData.getData('Text');
    }

    if (e.clipboardData) {
        return e.clipboardData.types.length > 0
            ? e.clipboardData.getData(e.clipboardData.types[0])
            : e.clipboardData.getData('text/plain');
    }
    return;
}

TagsInput.prototype.handlePaste = function(e: ClipboardEvent): void {
    const {addOnPaste, pasteSplit}: {addOnPaste: boolean, pasteSplit: (data: string) => string[]} = this.props;

    if (!addOnPaste) {
        return;
    }

    e.preventDefault();

    const data = getClipboardData(e);
    if (data) {
        const tags = pasteSplit(data).map(tag => this._makeTag(tag));

        this._addTags(tags);
    }
};

const messages = defineMessages({
    emailError: {
        id: 'web.components.sharing.sharingForm.emailInvalid',
        defaultMessage: 'Please enter a valid email.'
    },
    emailPlaceholder: {
        id: 'web.components.sharing.sharingForm.emailPlaceholder',
        defaultMessage: 'PLEASE ENTER EMAILS'
    },
});

const keyCodes = {
    tab: 9,
    enter: 13,
    space: 32,
    semicolon: 186,
    comma: 188
};

interface UntranslatedOwnProps {
    sharingTypes: SharingType[];
    bookingId: string;
}
type SharingFormOwnProps = UntranslatedOwnProps & InjectedIntlProps;

type SharingFormStateProps = RequestStateProps & {
    previousUserEmails: string[];
    locale: LocaleCode;
};

interface SharingFormDispatchProps {
    share: (bookingId: string, emails: string[], sharingTypes: SharingType[], locale: LocaleCode) => void;
    resetErrors: () => void;
}

interface SharingFormInternalState {
    tags: string[];
    currentValue: string;
    showEmailHistory: boolean;
}

type SharingFormProps = SharingFormOwnProps & SharingFormStateProps & SharingFormDispatchProps;

export class SharingFormPresentational extends React.Component<SharingFormProps, SharingFormInternalState> {

    private tagsInput: TagsInput | null;
    private shareFormWrapper: HTMLDivElement | null;

    constructor(props: SharingFormProps) {
        super(props);
        this.state = {
            tags: [],
            currentValue: '',
            showEmailHistory: true
        };
    }

    componentDidMount(): void {
        document.addEventListener('click', this.closeEmailHistoryOnClickOffInput, true);
    }

    componentWillUnmount(): void {
        document.removeEventListener('click', this.closeEmailHistoryOnClickOffInput, true);
        this.props.resetErrors();
    }

    handleChange = (tags: string[]) => {
        this.setState({
            tags: this.getUniqueEmails(tags)
        });
        this.props.resetErrors();
    }

    handleTyping = (value: string) => {
        this.setState({
            currentValue: value
        });
    }

    handleEmailClick = (email: string) => {
        if (!!this.tagsInput) {
            this.tagsInput.addTag(email);
            this.tagsInput.focus();
        }
    }

    private closeEmailHistoryOnClickOffInput = (event: Event) => {
        const target = event.target as Node;
        if (!!!!this.shareFormWrapper
            && !this.shareFormWrapper.contains(target)) {
            this.setEmailHistoryVisibility(false);
        }
    }

    shareTags = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        this.props.resetErrors();

        if (!this.props.isLoading ) {
            // Imitate the tagsinput behaviour for the current input.
            // This allows us to skip going through the state change and re-render,
            // which would complicate the logic here.
            const currentInput = this.getInputValue();
            this.clearInput();

            const emailsToShareWith = this.getUniqueEmails(this.state.tags.concat(currentInput));

            // Share the booking with the selected emails, if there are any.
            if (emailsToShareWith.length > 0) {
                this.props.share(this.props.bookingId, emailsToShareWith, this.props.sharingTypes, this.props.locale);

                this.setState({ tags: [] });
            }
        }
    }

    private getUniqueEmails(tags: string[]): string[] {
        const emailTags = tags.filter(tag => isEmail(tag));
        return getUniqueElements(emailTags, email => email.toLowerCase());
    }

    getInputValue(): string {
        if (!this.tagsInput) {
            return '';
        }

        const input: HTMLInputElement = (this.tagsInput as any).input;

        return input.value;
    }

    clearInput(): void {
        if (this.tagsInput) {
            this.tagsInput.clearInput();
        }
    }

    splitPastedText = (data: string) => {
        return data
            .replace(/[\r\n,;]/g, ' ')
            .split(' ')
            .map(d => d.trim())
            .map(d => d.replace(/^\<|\>$/g, '')) // remove <> around the address when pasted from an email client
            .filter(d => d !== '');
    }

    customRenderLayout = (tagComponents: React.ReactNode, inputComponent: React.ReactNode) => {
        return (
            <div className={style.LayoutTagsInput}>
                <div className={style.TagContainer}>
                    {tagComponents}
                </div>
                {inputComponent}
            </div>
        );
    }

    disableInputBlurOnMouseClick = (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault();

    setEmailHistoryVisibility(set: boolean): void {
        this.setState({showEmailHistory: set});
    }

    private getServerValidationErrorMessages(): string[] {
        const validationErrors = (this.props.requestError && this.props.requestError.validationErrors) || {};

        const errorArrays = Object.keys(validationErrors).map(key => validationErrors[key]);

        const errors = errorArrays.reduce((result, current) => result.concat(current), []);

        return errors.map(err => err.message);
    }

    render(): JSX.Element {
        return (
            <div className={style.SharingForm}>
                <div ref={wrapper => this.shareFormWrapper = wrapper}>
                    <form
                        noValidate={true}
                        onSubmit={this.shareTags}
                    >
                        <TagsInput
                            ref={renderedInput => this.tagsInput = renderedInput}
                            value={this.state.tags}
                            addKeys={[
                                keyCodes.tab,
                                keyCodes.enter,
                                keyCodes.space,
                                keyCodes.semicolon,
                                keyCodes.comma
                            ]}
                            addOnPaste={true}
                            pasteSplit={this.splitPastedText}
                            onChange={this.handleChange}
                            onChangeInput={this.handleTyping}
                            inputValue={this.state.currentValue}
                            renderLayout={this.customRenderLayout}
                            inputProps={{
                                placeholder: this.props.intl.formatMessage(messages.emailPlaceholder),
                                type: 'email',
                                formNoValidate: true,
                                autoFocus: true,
                                onFocus: () => { this.setEmailHistoryVisibility(true); }
                            }}
                        />
                        <ButtonWithLoadingIndicator
                            isLoading={this.props.isLoading}
                            onMouseDown={this.disableInputBlurOnMouseClick}
                        >
                            <FormattedMessage
                                id={'web.components.sharing.sharingForm.shareButton'}
                                defaultMessage={'SHARE'}
                            />
                        </ButtonWithLoadingIndicator>
                    </form>
                    <EmailHistoryDisplay
                        emails={this.props.previousUserEmails}
                        textFilter={this.state.currentValue}
                        tagFilter={this.state.tags}
                        visible={this.state.showEmailHistory}
                        clickHandler={this.handleEmailClick}
                    />
                </div>
                <ErrorsDisplay errors={this.getServerValidationErrorMessages()} />
                <RequestErrorMessage className={style.SharingErrorDisplay} requestState={this.props} />
            </div>
        );
    }
}

function mapStateToProps(
    state: AppState, ownProps: UntranslatedOwnProps, getRequestError: (request: RequestClass<any>) => RequestStateProps
): SharingFormStateProps {
    const previousUserEmails = state.previousEmails.emails;
    const locale = state.intl.locale;
    return {
        ...getRequestError(ShareRequest),
        previousUserEmails,
        locale
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: UntranslatedOwnProps)
    : SharingFormDispatchProps {
    return {
        share: (bookingId: string, emails: string[], sharingTypes: SharingType[], locale: LocaleCode) =>
            dispatch(new FetchRequest(new ShareRequest(bookingId, emails, sharingTypes, locale))),
        resetErrors: () => dispatch(new FetchResetStatus(ShareRequest))
    };
}

export const SharingForm = connectWithRequests(mapStateToProps, mapDispatchToProps)
    (injectIntl(SharingFormPresentational));
